/**
 * 问答面板组件
 * 提供提问界面、对话列表和消息记录
 * 优化内容：性能优化、智能标题生成、延迟保存、批量删除、置顶功能
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Modal, Checkbox, Dropdown, Menu } from 'antd';
import { conversationAPI } from '../services/api';

// SVG 图标组件
const SendIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13"/>
    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
  </svg>
);

const LoaderIcon = () => (
  <svg className="animate-spin" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
  </svg>
);

const MessageCircleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
  </svg>
);

const MessageSquareIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-400">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
);

const BotIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 8V4H8"/>
    <rect x="4" y="8" width="16" height="12" rx="2"/>
    <path d="M2 14h2"/>
    <path d="M20 14h2"/>
    <path d="M15 13v2"/>
    <path d="M9 13v2"/>
  </svg>
);

const UserIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);

const SparklesIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/>
  </svg>
);

const PlusIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"/>
    <line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
  </svg>
);

const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>
    <path d="m15 5 4 4"/>
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/>
    <path d="m21 21-4.35-4.35"/>
  </svg>
);

const MoreIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="1"/>
    <circle cx="12" cy="5" r="1"/>
    <circle cx="12" cy="19" r="1"/>
  </svg>
);

const BatchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7"/>
    <rect x="14" y="3" width="7" height="7"/>
    <rect x="14" y="14" width="7" height="7"/>
    <rect x="3" y="14" width="7" height="7"/>
  </svg>
);

const PinIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 19V6l4-3H8l4 3v13Z"/>
    <path d="M9 12l2 2 4-4"/>
  </svg>
);

// 格式化时间工具函数
// 后端返回的是 UTC 时间（不带时区信息），需要正确转换为本地时间
const formatTime = (dateString) => {
  if (!dateString) return '';
  // 后端返回的是 UTC 时间，需要确保正确解析
  let date;
  if (dateString.includes('Z') || dateString.includes('+')) {
    // 已经有时区信息
    date = new Date(dateString);
  } else {
    // 没有时区信息，假设是 UTC 时间，添加 'Z' 后缀
    date = new Date(dateString + 'Z');
  }
  if (isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('zh-CN', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: false,
    timeZone: 'Asia/Shanghai'
  });
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  // 后端返回的是 UTC 时间，需要确保正确解析
  let date;
  if (dateString.includes('Z') || dateString.includes('+')) {
    date = new Date(dateString);
  } else {
    date = new Date(dateString + 'Z');
  }
  if (isNaN(date.getTime())) return '';
  
  const now = new Date();
  // 确保两个日期在同一天进行比较（使用上海时区）
  const dateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const nowStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diff = nowStart - dateStart;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  
  if (days < 0) return date.toLocaleDateString('zh-CN', { timeZone: 'Asia/Shanghai' });
  if (days === 0) {
    // 今天，显示具体时间
    return formatTime(dateString);
  }
  if (days === 1) return '昨天';
  if (days < 7) return `${days}天前`;
  return date.toLocaleDateString('zh-CN', { timeZone: 'Asia/Shanghai' });
};

// 生成对话标题
const generateTitle = (content) => {
  if (!content || !content.trim()) return '新对话';
  const trimmed = content.trim();
  return trimmed.length > 30 ? trimmed.substring(0, 30) + '...' : trimmed;
};

// 消息项组件
const MessageItem = React.memo(({ msg }) => {
  return (
    <div
      key={msg.id}
      className={`flex items-start space-x-4 animate-slide-up ${
        msg.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''
      } message-new`}
    >
      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
        msg.role === 'user'
          ? 'bg-gradient-to-r from-pink-500 to-purple-500'
          : 'bg-gradient-to-r from-blue-500 to-purple-500'
      }`}>
        {msg.role === 'user' ? <UserIcon /> : <BotIcon />}
      </div>
      <div className={`max-w-[70%] ${msg.role === 'user' ? 'text-right' : ''}`}>
        <div className={`mb-1 flex items-center space-x-2 ${msg.role === 'user' ? 'justify-end' : ''}`}>
          <span className="text-xs font-medium text-gray-400">
            {msg.role === 'user' ? '您' : 'AI 助手'}
          </span>
          <span className="text-xs text-gray-500">
            {formatTime(msg.created_at)}
          </span>
        </div>
        <div className={`p-3 rounded-lg transition-all duration-300 ${
          msg.role === 'user'
            ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
            : msg.isError
              ? 'bg-red-500/20 text-red-300 border border-red-500/30'
              : 'bg-white/10 text-gray-300'
        }`}>
          <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
        </div>
      </div>
    </div>
  );
});

// 对话项组件（支持多选和菜单）- 使用React.memo减少不必要重渲染
const ConversationItem = React.memo(({ conv, isSelected, isChecked, onSelect, onEdit, onDelete, onToggleSelect, onPin, showCheckbox }) => {
  // 三点菜单
  const menu = (
    <Menu
      items={[
        {
          key: 'edit',
          label: (<span className="flex items-center space-x-2">
            <EditIcon />
            <span>编辑名称</span>
          </span>),
          onClick: () => onEdit(conv),
        },
        {
          key: 'pin',
          label: (<span className="flex items-center space-x-2">
            <PinIcon />
            <span>{conv.is_pinned ? '取消置顶' : '置顶'}</span>
          </span>),
          onClick: () => onPin(conv.id),
        },
        {
          type: 'divider',
        },
        {
          key: 'delete',
          label: (<span className="flex items-center space-x-2 text-red-400">
            <TrashIcon />
            <span>删除</span>
          </span>),
          onClick: () => onDelete(conv.id),
        },
      ]}
    />
  );

  return (
    <div
      key={conv.id}
      onClick={() => onSelect(conv)}
      className={`relative p-3 rounded-xl cursor-pointer transition-all duration-200 group flex items-start space-x-2 gpu-accelerate will-change-all layer-optimize ${
        isSelected
          ? 'bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-500/30 scale-[1.02]'
          : 'bg-[#1a1a2e]/80 hover:bg-[#252542] border border-transparent hover:scale-[1.01]'
      }`}
    >
      {/* 复选框（只在批量模式下显示） */}
      {showCheckbox && (
        <div className="flex-shrink-0 mt-0.5" onClick={(e) => e.stopPropagation()}>
          <Checkbox 
            checked={isChecked}
            onChange={(e) => onToggleSelect(conv.id)}
            className="bg-white/10 border-white/20 rounded"
          />
        </div>
      )}
      
      {/* 标题区域 */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center space-x-2">
          {/* 置顶标记 */}
          {conv.is_pinned && (
            <PinIcon className="w-3.5 h-3.5 text-yellow-400 flex-shrink-0" />
          )}
          <p className="text-sm font-medium text-gray-200 truncate">
            {conv.title || '新对话'}
          </p>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {formatDate(conv.updated_at)}
        </p>
      </div>
      
      {/* 三点菜单 */}
      <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        <Dropdown overlay={menu} trigger={['click']}>
          <button
            onClick={(e) => e.stopPropagation()}
            className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded transition-colors"
          >
            <MoreIcon />
          </button>
        </Dropdown>
      </div>
    </div>
  );
});

// 主组件
const QAPanel = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isConversationsLoading, setIsConversationsLoading] = useState(true);
  const [showEditTitle, setShowEditTitle] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [messageStatus, setMessageStatus] = useState('');
  const [selectedIds, setSelectedIds] = useState([]); // 批量选择的对话ID
  const [showCheckbox, setShowCheckbox] = useState(false); // 是否显示复选框（批量模式）
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const hasSentMessage = useRef(false);

  // 获取对话列表
  const fetchConversations = useCallback(async () => {
    try {
      const response = await conversationAPI.list();
      // 先按置顶排序，再按更新时间排序
      // 从API获取的对话需要添加 isTemp: false，因为数据库中没有这个字段
      const sortedConversations = response.data.map(conv => ({
        ...conv,
        isTemp: false
      })).sort((a, b) => {
        // 置顶的排在前面
        if (a.is_pinned && !b.is_pinned) return -1;
        if (!a.is_pinned && b.is_pinned) return 1;
        // 同级别按更新时间排序
        return new Date(b.updated_at) - new Date(a.updated_at);
      });
      setConversations(sortedConversations);
      
      if (sortedConversations.length === 0) {
        // 新用户没有对话时，保持对话列表为空，但显示可输入的新对话界面
        const tempConversation = {
          id: 'temp-' + Date.now(),
          title: '新对话',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          isTemp: true,
          is_pinned: false
        };
        // 不设置到对话列表，只设置选中状态
        setSelectedConversation(tempConversation);
        setMessages([]);
      } else {
        setSelectedConversation(sortedConversations[0]);
        fetchMessages(sortedConversations[0].id);
      }
    } catch (error) {
      console.error('获取对话列表失败:', error);
    } finally {
      setIsConversationsLoading(false);
    }
  }, []);

  // 初始化时获取对话列表
  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const fetchMessages = async (conversationId) => {
    if (String(conversationId).startsWith('temp-')) {
      setMessages([]);
      return;
    }
    
    try {
      const response = await conversationAPI.get(conversationId);
      setMessages(response.data.messages);
    } catch (error) {
      console.error('获取消息失败:', error);
    }
  };

  const createNewConversation = useCallback(() => {
    // 检查是否已经存在一个临时对话
    const existingTemp = conversations.find(conv => conv.isTemp);
    
    if (existingTemp) {
      // 如果存在临时对话，直接跳转到该对话（不管是否发送过消息）
      setSelectedConversation(existingTemp);
      setMessages([]);
      hasSentMessage.current = false;
      setSelectedIds([]); // 清空选择
      setTimeout(() => inputRef.current?.focus(), 100);
      return;
    }
    
    // 如果不存在临时对话，创建新的临时对话
    const tempConversation = {
      id: 'temp-' + Date.now(),
      title: '新对话',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      isTemp: true,
      is_pinned: false
    };
    setConversations(prev => [tempConversation, ...prev]);
    setSelectedConversation(tempConversation);
    setMessages([]);
    hasSentMessage.current = false;
    setSelectedIds([]); // 清空选择
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [conversations]);

  // 批量删除选中的对话
  const handleBatchDelete = useCallback(() => {
    if (selectedIds.length === 0) return;
    
    Modal.confirm({
      title: '确认批量删除',
      content: `确定要删除选中的 ${selectedIds.length} 个对话吗？此操作无法撤销。`,
      okText: '确定删除',
      cancelText: '取消',
      onOk: async () => {
        try {
          // 分离临时对话和真实对话
          const realIds = selectedIds.filter(id => !String(id).startsWith('temp-'));
          
          // 批量删除真实对话（使用新的批量删除 API）
          if (realIds.length > 0) {
            await conversationAPI.batchDelete(realIds);
          }
          
          // 更新对话列表
          setConversations(prev => prev.filter(conv => !selectedIds.includes(conv.id)));
          
          // 如果当前选中的对话被删除了
          if (selectedIds.includes(selectedConversation?.id)) {
            const remaining = conversations.filter(conv => !selectedIds.includes(conv.id));
            if (remaining.length > 0) {
              setSelectedConversation(remaining[0]);
              fetchMessages(remaining[0].id);
            } else {
              // 删除最后一条对话后，保持空白状态
              setSelectedConversation(null);
              setMessages([]);
            }
          }
          
          // 清空选择并退出批量模式
          setSelectedIds([]);
          setShowCheckbox(false);
        } catch (error) {
          console.error('批量删除失败:', error);
          Modal.error({ title: '删除失败', content: '请重试' });
        }
      }
    });
  }, [selectedIds, selectedConversation, conversations, createNewConversation]);

  // 切换选择单个对话
  const handleToggleSelect = useCallback((conversationId) => {
    setSelectedIds(prev => {
      if (prev.includes(conversationId)) {
        return prev.filter(id => id !== conversationId);
      } else {
        return [...prev, conversationId];
      }
    });
  }, []);

  // 全选/取消全选
  const handleSelectAll = useCallback(() => {
    const filtered = searchQuery.trim() 
      ? conversations.filter(conv => 
          (conv.title || '').toLowerCase().includes(searchQuery.toLowerCase())
        )
      : conversations;
    if (selectedIds.length === filtered.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filtered.map(conv => conv.id));
    }
  }, [selectedIds, conversations, searchQuery]);

  // 进入批量模式
  const handleEnterBatchMode = useCallback(() => {
    setShowCheckbox(true);
  }, []);

  // 退出批量模式
  const handleExitBatchMode = useCallback(() => {
    setShowCheckbox(false);
    setSelectedIds([]);
  }, []);

  // 置顶/取消置顶对话
  const handlePinConversation = useCallback(async (conversationId) => {
    if (String(conversationId).startsWith('temp-')) {
      // 临时对话只在本地处理
      setConversations(prev => prev.map(conv => 
        conv.id === conversationId 
          ? { ...conv, is_pinned: !conv.is_pinned }
          : conv
      ).sort((a, b) => {
        if (a.is_pinned && !b.is_pinned) return -1;
        if (!a.is_pinned && b.is_pinned) return 1;
        return new Date(b.updated_at) - new Date(a.updated_at);
      }));
      
      // 更新选中的对话
      setSelectedConversation(prev => 
        prev?.id === conversationId 
          ? { ...prev, is_pinned: !prev.is_pinned }
          : prev
      );
      return;
    }
    
    try {
      // 获取当前状态
      const conversation = conversations.find(conv => conv.id === conversationId);
      const newPinState = !conversation?.is_pinned;
      
      // 调用API更新置顶状态
      await conversationAPI.updatePin(conversationId, newPinState);
      
      // 更新本地状态
      setConversations(prev => prev.map(conv => 
        conv.id === conversationId 
          ? { ...conv, is_pinned: newPinState }
          : conv
      ).sort((a, b) => {
        if (a.is_pinned && !b.is_pinned) return -1;
        if (!a.is_pinned && b.is_pinned) return 1;
        return new Date(b.updated_at) - new Date(a.updated_at);
      }));
      
      // 更新选中的对话
      setSelectedConversation(prev => 
        prev?.id === conversationId 
          ? { ...prev, is_pinned: newPinState }
          : prev
      );
      
      Modal.success({ 
        title: '操作成功', 
        content: newPinState ? '对话已置顶' : '已取消置顶',
        duration: 1.5
      });
    } catch (error) {
      console.error('置顶操作失败:', error);
      Modal.error({ title: '操作失败', content: '请重试' });
    }
  }, [conversations]);

  // 使用 useCallback 缓存回调函数
  const handleSendMessage = useCallback(async (e) => {
    e.preventDefault();
    
    if (!inputMessage.trim() || !selectedConversation || isLoading) return;
    
    setIsLoading(true);
    setMessageStatus('sending');
    
    const messageContent = inputMessage.trim();
    setInputMessage('');
    
    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: messageContent,
      created_at: new Date().toISOString(),
      isNew: true
    };
    setMessages(prev => [...prev, userMessage]);
    
    try {
      setMessageStatus('thinking');
      
      let conversationId = selectedConversation.id;
      
      if (selectedConversation.isTemp) {
        console.log('=== 发送消息 - 临时对话 ===');
        // 如果用户已经编辑过标题（不是默认的"新对话"），使用用户编辑的标题，否则自动生成
        const title = selectedConversation.title && selectedConversation.title !== '新对话' 
          ? selectedConversation.title 
          : generateTitle(messageContent);
        console.log('创建对话，标题:', title);
        const createResponse = await conversationAPI.create(title);
        conversationId = createResponse.data.id;
        console.log('对话创建成功，ID:', conversationId);
        
        setConversations(prev => {
          const updated = prev.filter(conv => conv.id !== selectedConversation.id);
          return [{
            id: conversationId,
            title,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            isTemp: false,
            is_pinned: false
          }, ...updated].sort((a, b) => {
            if (a.is_pinned && !b.is_pinned) return -1;
            if (!a.is_pinned && b.is_pinned) return 1;
            return new Date(b.updated_at) - new Date(a.updated_at);
          });
        });
        
        setSelectedConversation({
          id: conversationId,
          title,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          isTemp: false,
          is_pinned: false
        });
        console.log('=== 发送消息 - 临时对话转换完成 ===');
      }
      
      const response = await conversationAPI.sendMessage(conversationId, messageContent);
      
      const assistantMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: response.data.answer,
        source_doc_ids: response.data.source_doc_ids,
        created_at: new Date().toISOString(),
        isNew: true
      };
      setMessages(prev => [...prev, assistantMessage]);
      setMessageStatus('done');
      
      if (!selectedConversation.isTemp) {
        setConversations(prev => {
          const updated = prev.map(conv => 
            conv.id === conversationId 
              ? { ...conv, updated_at: new Date().toISOString() }
              : conv
          );
          return updated.sort((a, b) => {
            if (a.is_pinned && !b.is_pinned) return -1;
            if (!a.is_pinned && b.is_pinned) return 1;
            return new Date(b.updated_at) - new Date(a.updated_at);
          });
        });
      }
      
      hasSentMessage.current = true;
    } catch (error) {
      console.error('发送消息失败:', error);
      setMessageStatus('error');
      const errorMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: '抱歉，发送失败，请稍后重试。',
        created_at: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      setTimeout(() => setMessageStatus(''), 3000);
    }
  }, [inputMessage, selectedConversation, isLoading]);

  const handleSelectConversation = useCallback((conversation) => {
    setSelectedConversation(conversation);
    fetchMessages(conversation.id);
    hasSentMessage.current = conversation.messages?.length > 0;
  }, []);

  const handleDeleteConversation = useCallback((conversationId) => {
    console.log('删除对话:', conversationId, '是否临时:', String(conversationId).startsWith('temp-'));
    
    if (String(conversationId).startsWith('temp-')) {
      console.log('删除临时对话，不调用接口');
      setConversations(prev => prev.filter(conv => conv.id !== conversationId));
      setSelectedIds(prev => prev.filter(id => id !== conversationId));
      if (selectedConversation?.id === conversationId) {
        const remaining = conversations.filter(conv => conv.id !== conversationId);
        if (remaining.length > 0) {
          setSelectedConversation(remaining[0]);
          fetchMessages(remaining[0].id);
        } else {
          // 删除最后一条对话后，保持空白状态
          setSelectedConversation(null);
          setMessages([]);
        }
      }
      return;
    }
    
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这个对话吗？此操作无法撤销。',
      okText: '确定删除',
      cancelText: '取消',
      onOk: async () => {
        try {
          console.log('调用删除接口:', conversationId);
          await conversationAPI.delete(conversationId);
          console.log('删除接口调用成功');
          setConversations(prev => prev.filter(conv => conv.id !== conversationId));
          setSelectedIds(prev => prev.filter(id => id !== conversationId));
          if (selectedConversation?.id === conversationId) {
            const remaining = conversations.filter(conv => conv.id !== conversationId);
            if (remaining.length > 0) {
              setSelectedConversation(remaining[0]);
              fetchMessages(remaining[0].id);
            } else {
              // 删除最后一条对话后，保持空白状态
              setSelectedConversation(null);
              setMessages([]);
            }
          }
        } catch (error) {
          console.error('删除对话失败:', error);
          Modal.error({ title: '删除失败', content: '请重试' });
        }
      }
    });
  }, [selectedConversation, conversations, createNewConversation]);

  const handleUpdateTitle = useCallback(() => {
    if (!editTitle.trim() || !selectedConversation) {
      console.log('更新标题失败: 标题为空或没有选中对话');
      return;
    }
    
    console.log('=== 更新标题开始 ===');
    console.log('编辑的标题:', editTitle);
    console.log('选中的对话:', JSON.stringify(selectedConversation));
    console.log('对话ID:', selectedConversation.id, '是否临时:', selectedConversation.isTemp);
    
    if (selectedConversation.isTemp) {
      console.log('对话是临时对话，只更新本地状态');
      setConversations(prev => prev.map(conv => 
        conv.id === selectedConversation.id 
          ? { ...conv, title: editTitle }
          : conv
      ));
      setSelectedConversation(prev => ({ ...prev, title: editTitle }));
      setShowEditTitle(null);
      setEditTitle('');
      console.log('=== 更新标题结束 - 临时对话 ===');
      return;
    }
    
    console.log('调用API更新标题到数据库');
    conversationAPI.update(selectedConversation.id, editTitle)
      .then(() => {
        console.log('标题更新成功 - API调用成功');
        setConversations(prev => prev.map(conv => 
          conv.id === selectedConversation.id 
            ? { ...conv, title: editTitle }
            : conv
        ));
        setSelectedConversation(prev => ({ ...prev, title: editTitle }));
        setShowEditTitle(null);
        setEditTitle('');
        console.log('=== 更新标题结束 - API调用成功 ===');
      })
      .catch(error => {
        console.error('更新标题失败:', error);
        console.log('=== 更新标题结束 - API调用失败 ===');
      });
  }, [editTitle, selectedConversation]);

  const handleEditTitle = useCallback((conv) => {
    setEditTitle(conv.title || '');
    setShowEditTitle(conv.id);
  }, []);

  // 滚动到底部
  useEffect(() => {
    if (messagesEndRef.current) {
      requestAnimationFrame(() => {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
      });
    }
  }, [messages]);

  // 键盘快捷键
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        if (!isLoading && inputMessage.trim()) {
          handleSendMessage(e);
        }
      }
      if (e.key === 'Escape') {
        setInputMessage('');
        inputRef.current?.blur();
        // 按ESC退出批量模式
        if (showCheckbox) {
          handleExitBatchMode();
        }
      }
      // Ctrl+A 全选（仅在批量模式下）
      if ((e.ctrlKey || e.metaKey) && e.key === 'a' && showCheckbox) {
        e.preventDefault();
        handleSelectAll();
      }
      // Delete 键删除选中项（仅在批量模式下）
      if (e.key === 'Delete' && selectedIds.length > 0) {
        e.preventDefault();
        handleBatchDelete();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [inputMessage, isLoading, handleSendMessage, handleSelectAll, handleBatchDelete, selectedIds, showCheckbox, handleExitBatchMode]);

  // 使用 useMemo 缓存过滤结果
  const filteredConversations = useMemo(() => {
    if (!searchQuery.trim()) return conversations;
    return conversations.filter(conv => 
      (conv.title || '').toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [conversations, searchQuery]);

  // 渲染编辑标题输入框
  const renderEditTitleInput = useCallback((conv) => {
    if (showEditTitle !== conv.id) return null;
    
    const handleBlur = () => {
      // 使用 setTimeout 延迟清空，让保存按钮有时间处理点击事件
      setTimeout(() => {
        setShowEditTitle(null);
        setEditTitle('');
      }, 100);
    };
    
    return (
      <div className="mt-2 animate-fade-in">
        <input
          type="text"
          value={editTitle}
          onChange={(e) => setEditTitle(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') handleUpdateTitle();
          }}
          onBlur={handleBlur}
          className="w-full px-3 py-2 bg-white/10 border border-blue-500/30 rounded-lg text-sm text-white focus:outline-none focus:border-blue-500"
          autoFocus
        />
        <div className="flex justify-end space-x-2 mt-2">
          <button
            onClick={() => {
              setShowEditTitle(null);
              setEditTitle('');
            }}
            className="px-3 py-1 text-xs text-gray-400 hover:text-white transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleUpdateTitle}
            className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            保存
          </button>
        </div>
      </div>
    );
  }, [showEditTitle, editTitle, handleUpdateTitle]);

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex gap-6">
        {/* 对话列表侧边栏 */}
        <div className="w-80 flex-shrink-0">
          <div className="glass-card p-4 h-full sticky top-4 bg-[#0f0f23]/99 border border-white/5">
            {/* 头部 */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-200">对话列表</h2>
              <button
                onClick={createNewConversation}
                className="p-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg hover:shadow-lg hover:shadow-blue-500/30 transition-all hover:scale-105 active:scale-95"
                title="新建对话"
              >
                <PlusIcon />
              </button>
            </div>
            
            {/* 搜索框 - 图标在输入框内部 */}
            <div className="relative mb-4">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <SearchIcon className="text-gray-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜索对话..."
                className="w-full pl-9 pr-4 py-2.5 bg-[#1a1a2e] border border-white/10 rounded-xl text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 text-sm"
              />
            </div>
            
            {/* 批量操作栏 */}
            {selectedIds.length > 0 && (
              <div className="flex items-center justify-between mb-3 p-2 bg-blue-500/10 rounded-lg">
                <span className="text-sm text-gray-300">已选择 {selectedIds.length} 项</span>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleSelectAll}
                    className="px-2 py-1 text-xs text-gray-400 hover:text-white transition-colors"
                  >
                    全选
                  </button>
                  <button
                    onClick={handleExitBatchMode}
                    className="px-2 py-1 text-xs text-gray-400 hover:text-white transition-colors"
                  >
                    取消
                  </button>
                  <button
                    onClick={handleBatchDelete}
                    className="px-3 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 transition-colors flex items-center space-x-1"
                  >
                    <TrashIcon />
                    <span>删除</span>
                  </button>
                </div>
              </div>
            )}
            
            {/* 批量操作入口（未选中任何项时显示） */}
            {!showCheckbox && selectedIds.length === 0 && filteredConversations.length > 0 && (
              <div className="flex items-center justify-between mb-3">
                <button
                  onClick={handleEnterBatchMode}
                  className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                >
                  <BatchIcon />
                  <span>批量操作</span>
                </button>
              </div>
            )}
            
            {/* 全选复选框（批量模式下显示） */}
            {showCheckbox && selectedIds.length === 0 && (
              <div className="flex items-center mb-3">
                <Checkbox 
                  checked={selectedIds.length === filteredConversations.length && filteredConversations.length > 0}
                  onChange={handleSelectAll}
                  className="bg-white/10 border-white/20 rounded"
                />
                <span className="text-xs text-gray-400 ml-2">全选</span>
              </div>
            )}
            
            {/* 对话列表 */}
            {isConversationsLoading ? (
              <div className="flex justify-center py-8">
                <LoaderIcon />
              </div>
            ) : (
              <div className="space-y-1.5 max-h-[calc(100vh-280px)] overflow-y-auto scrollbar-hidden bg-[#0f0f23]/99 will-change-scroll contain-layout gpu-accelerate scroll-smooth">
                {filteredConversations.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageCircleIcon className="w-12 h-12 text-gray-500 mx-auto mb-2" />
                    <p className="text-gray-400 text-sm">没有找到匹配的对话</p>
                  </div>
                ) : (
                  filteredConversations.map((conv) => (
                    <React.Fragment key={conv.id}>
                      <ConversationItem
                        conv={conv}
                        isSelected={selectedConversation?.id === conv.id}
                        isChecked={selectedIds.includes(conv.id)}
                        onSelect={handleSelectConversation}
                        onEdit={handleEditTitle}
                        onDelete={handleDeleteConversation}
                        onToggleSelect={handleToggleSelect}
                        onPin={handlePinConversation}
                        showCheckbox={showCheckbox}
                      />
                      {renderEditTitleInput(conv)}
                    </React.Fragment>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        {/* 对话内容区域 */}
        <div className="flex-1">
          {selectedConversation ? (
            <div className="glass-card glow-blue h-full flex flex-col bg-[#0f0f23]/99 border border-white/5">
              {/* 对话头部 */}
              <div className="px-6 py-4 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-white">{selectedConversation.title || '新对话'}</h2>
                  <div className="flex items-center space-x-3">
                    <span className="text-xs text-gray-500 bg-white/5 px-3 py-1 rounded-full">
                      Ctrl/Cmd + Enter 发送
                    </span>
                    <button
                      onClick={createNewConversation}
                      className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-medium hover:shadow-lg hover:shadow-blue-500/30 transition-all hover:scale-105 active:scale-95"
                    >
                      <PlusIcon />
                      <span className="text-sm">新建对话</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* 消息列表 */}
              <div 
                ref={messagesContainerRef}
                className="flex-1 overflow-y-auto p-6 space-y-6 scrollbar-hidden bg-[#0f0f23]/99 will-change-scroll contain-layout gpu-accelerate scroll-smooth"
              >
                {messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center">
                    <div className="w-20 h-20 mb-6 animate-float">
                      <SparklesIcon />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">欢迎使用智能问答系统</h3>
                    <p className="text-gray-400">上传文档后，我可以基于知识库为您提供精准的回答</p>
                    <div className="mt-6 flex items-center space-x-4 text-sm text-gray-500">
                      <span>💡 支持 Markdown 格式</span>
                      <span>⌨️ Ctrl/Cmd + Enter 发送</span>
                    </div>
                  </div>
                ) : (
                  messages.map((msg) => (
                    <MessageItem key={msg.id} msg={msg} />
                  ))
                )}
                <div ref={messagesEndRef} />
                
                {/* 加载状态 */}
                {isLoading && (
                  <div className="flex items-start space-x-4 animate-fade-in">
                    <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center flex-shrink-0 animate-pulse">
                      <BotIcon />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="font-semibold text-white">AI 助手</span>
                        <span className="text-xs text-gray-500">正在思考...</span>
                      </div>
                      <div className="typing-indicator">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* 输入区域 */}
              <div className="px-6 py-4 border-t border-white/10">
                <form onSubmit={handleSendMessage}>
                  <div className="flex space-x-3">
                    <input
                      ref={inputRef}
                      type="text"
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      placeholder="输入您的问题..."
                      disabled={isLoading}
                      className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <button
                      type="submit"
                      disabled={!inputMessage.trim() || isLoading}
                      className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl font-medium hover:shadow-lg hover:shadow-blue-500/30 transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                    >
                      <SendIcon />
                    </button>
                  </div>
                  {messageStatus === 'error' && (
                    <p className="mt-2 text-red-400 text-sm">发送失败，请重试</p>
                  )}
                </form>
              </div>
            </div>
          ) : (
            // 没有选中对话时显示的界面
            <div className="w-full h-full flex flex-col items-center justify-center p-8">
              <div className="w-32 h-32 mb-8 rounded-full bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center border border-white/10">
                <MessageSquareIcon />
              </div>
              <h3 className="text-2xl font-semibold text-white mb-4">还没有对话</h3>
              <p className="text-gray-400 text-center max-w-md">点击左侧"+"按钮创建新对话，开始与AI助手交流</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QAPanel;